import React, { useState } from 'react';
import { Modal, LoadingOverlay, Tabs, Group, Button } from '@mantine/core';
import { GroupMember } from '../../../types/group';
import { ExpenseForm } from './ExpenseForm';
import { ReceiptScanner } from './ReceiptScanner';
import receiptIcon from '../../../assets/icons/receipt.svg';

export interface Expense {
    id?: number;
    description: string;
    amount: number;
    date: string;
    due_date?: string;
    paid_by: {
        id: string;
        display_name: string;
    };
    split_type: 'equal' | 'exact' | 'percentage' | 'shares' | 'items';
    split_details: Record<string, any>;
    additional_payers?: Record<string, { amount: number }>;
    category?: {
        id: string;
        name: string;
    };
    notes?: string;
    recurring: boolean;
    recurring_frequency?: string;
    recurring_end_date?: string;
    receipts?: Array<{
        id: string;
        url: string;
    }>;
    expense_image_url?: string;
    thumbnail_url?: string;
    status?: 'active' | 'draft' | 'deleted';
}

interface AddExpenseModalProps {
    opened: boolean;
    onClose: () => void;
    onSuccess: () => void;
    groupId: string;
    members: GroupMember[];
    accessToken: string;
    expense?: Expense;
}

export const AddExpenseModal = ({
    opened,
    onClose,
    onSuccess,
    groupId,
    members,
    accessToken,
    expense
}: AddExpenseModalProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>('manual');
    const isEditing = !!expense;

    // Prepare initial data to prefill form when editing
    const initialData = expense
        ? {
            description: expense.description ?? '',
            amount: expense.amount ?? 0,
            date: expense.date ? new Date(expense.date) : new Date(),
            receipt_image_url:
                expense.expense_image_url || expense.thumbnail_url || undefined,
        }
        : undefined;

    const handleClose = () => {
        setActiveTab('manual');
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isEditing ? "Edit Expense" : "Add Expense"}
            size="lg"
            centered
        >
            <div style={{ position: 'relative' }}>
                <LoadingOverlay visible={isSubmitting} />

                <Tabs
                    value={activeTab}
                    onChange={setActiveTab}
                    mb="md"
                >
                    <Tabs.List>
                        <Tabs.Tab value="manual">
                            Manual Entry
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="receipt"
                            leftSection={<img src={receiptIcon} alt="" width={14} />}
                            disabled={isEditing}
                        >
                            Scan Receipt
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="manual">
                        <ExpenseForm
                            groupId={groupId}
                            members={members}
                            accessToken={accessToken}
                            expense={expense}
                            onClose={handleClose}
                            onSuccess={onSuccess}
                            setIsSubmitting={setIsSubmitting}
                            initialData={initialData}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="receipt">
                        <ReceiptScanner
                            groupId={groupId}
                            members={members}
                            accessToken={accessToken}
                            onClose={handleClose}
                            onSuccess={onSuccess}
                            setIsSubmitting={setIsSubmitting}
                        />
                    </Tabs.Panel>
                </Tabs>
            </div>
        </Modal>
    );
}; 